/**
 * @architecture-header 1
 * @layer module
 * @domain reports
 * @responsibility Load context-scoped teacher students and backend-generated learning reports.
 * @state-owner TanStack Query
 * @allowed-imports same module model; app environment; contracts; TanStack Query
 * @forbidden routes; app bootstrap; direct generated SDK; Zustand entity copies
 * @public-api via modules/reports/index.ts
 * @side-effects authenticated GET requests outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); POST create -> useCreate<Resource>; action sub-resource -> use<Action><Resource>
 * @naming-hook PUT/PATCH entity -> useUpdate<Resource>; DELETE -> useDelete<Resource>
 * @naming-hook banned prefixes: useGet, useFetch, useLoad, useSet, useRemove, useSave, useDo
 * @naming-path plural lowercase nouns; /me for the caller; no version, role or verb segment; filters in the query string
 * @naming-body camelCase; never the caller's role and never a server-side key — the token says who, the path says what
 * @naming-rules docs/architecture/API_NAMING.md, all three enforced by pnpm api-naming:check
 * @tests modules/reports/model/run-report.test.ts and module-contracts.test.ts
 */

import { useQuery } from '@tanstack/react-query';
import {
  generatedReportResponseSchema,
  teacherStudentSchema,
  weakTopicsResponseSchema,
} from '@vispo/contracts';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import type { LearningReport } from '../model/types';

const examHistorySchema = z.object({
  entries: z.array(
    z
      .object({
        assignment_exam_paper_id: z.string().nullable().optional(),
        percentage: z.number().nullable().optional(),
        total_score: z.number().nullable().optional(),
        max_score: z.number().nullable().optional(),
        status: z.string(),
        submitted_at: z.string().nullable().optional(),
      })
      .loose(),
  ),
  average_percentage: z.number().nullable().optional(),
  total_exams: z.number().int().nonnegative(),
});

const demoReports: LearningReport[] = [
  {
    studentId: 'student-wang',
    studentName: '王柏睿',
    period: '7 月 1 日 – 7 月 31 日',
    periodStart: '2026-07-01',
    periodEnd: '2026-07-31',
    mastery: 84,
    completion: 88,
    focus: 81,
    studyHours: '2.5',
    examsCompleted: 3,
    questionsAnswered: 18,
    activities: [
      {
        id: 'run-live',
        activityId: 'activity-equation',
        title: '一元一次方程式・移項',
        dateLabel: '今天',
        contextLabel: '週三數學 A 班 · 課堂進行',
        resultLabel: '進行中',
      },
      {
        id: 'run-homework',
        activityId: 'activity-fraction',
        title: '分數係數練習',
        dateLabel: '今天',
        contextLabel: '週三數學 A 班 · 指派作業',
        resultLabel: '收件中',
      },
    ],
    trend: [52, 58, 61, 70, 76, 84],
    concepts: [
      { name: '列式', accuracy: 86 },
      { name: '合併同類項', accuracy: 74 },
      { name: '分數係數', accuracy: 63 },
      { name: '應用題', accuracy: 58 },
      { name: '移項變號', accuracy: 42 },
    ],
    suggestions: ['先用天平模型重教移項變號', '安排 4 題同型練習', '下一堂開場先做 3 分鐘診斷'],
  },
  {
    studentId: 'student-lin',
    studentName: '林宥辰',
    period: '7 月 1 日 – 7 月 31 日',
    periodStart: '2026-07-01',
    periodEnd: '2026-07-31',
    mastery: 64,
    completion: 82,
    focus: 75,
    studyHours: '1.8',
    examsCompleted: 2,
    questionsAnswered: 16,
    activities: [
      {
        id: 'run-homework-lin',
        activityId: 'activity-fraction',
        title: '分數係數練習',
        dateLabel: '今天',
        contextLabel: '週三數學 A 班 · 指派作業',
        resultLabel: '收件中',
      },
    ],
    trend: [52, 55, 59, 58, 63, 64],
    concepts: [
      { name: '列式', accuracy: 80 },
      { name: '合併同類項', accuracy: 69 },
      { name: '分數係數', accuracy: 55 },
      { name: '應用題', accuracy: 51 },
      { name: '移項變號', accuracy: 47 },
    ],
    suggestions: ['把長題幹拆成兩步', '先練整數係數再進分數', '每題完成後說出檢查步驟'],
  },
];

export const reportKeys = {
  list: (activeContextId: string, role: 'teacher' | 'student', range: 'week' | 'month') =>
    ['reports', activeContextId, role, range] as const,
};

function displayDate(value: string) {
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('zh-TW', { month: 'long', day: 'numeric' }).format(date);
}

export function useLearningReports(
  activeContextId: string,
  role: 'teacher' | 'student' = 'teacher',
  range: 'week' | 'month' = 'month',
  studentIdentity?: { id: string; name: string },
) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: reportKeys.list(activeContextId, role, range),
    queryFn: async (): Promise<LearningReport[]> => {
      if (environment.demoMode) return demoReports;
      if (role !== 'teacher') {
        if (!studentIdentity) return [];
        const studentId = encodeURIComponent(studentIdentity.id);
        const [historyValue, weaknessValue] = await Promise.all([
          environment.apiClient.request<unknown>(`/api/students/${studentId}/run-history`),
          environment.apiClient.request<unknown>(`/api/students/${studentId}/weak-concepts`),
        ]);
        const history = examHistorySchema.parse(historyValue);
        const weakness = z
          .object({
            topic_accuracy: z.record(z.string(), z.number()),
            total_questions_analyzed: z.number().int().nonnegative(),
          })
          .parse(weaknessValue);
        const trend = history.entries
          .flatMap((entry) => (entry.percentage == null ? [] : [Math.round(entry.percentage)]))
          .reverse();
        const end = new Date();
        const start = new Date(end);
        start.setDate(start.getDate() - (range === 'week' ? 7 : 30));
        const concepts = Object.entries(weakness.topic_accuracy)
          .map(([name, accuracy]) => ({ name, accuracy: Math.round(accuracy) }))
          .sort((left, right) => left.accuracy - right.accuracy);
        return [
          {
            studentId: studentIdentity.id,
            studentName: studentIdentity.name,
            period: `${displayDate(start.toISOString().slice(0, 10))} – ${displayDate(end.toISOString().slice(0, 10))}`,
            periodStart: start.toISOString().slice(0, 10),
            periodEnd: end.toISOString().slice(0, 10),
            mastery: Math.round(history.average_percentage ?? trend.at(-1) ?? 0),
            completion: history.total_exams > 0 ? 100 : 0,
            focus: 0,
            studyHours: '0',
            examsCompleted: history.total_exams,
            questionsAnswered: weakness.total_questions_analyzed,
            trend: trend.length ? trend : [0],
            concepts,
            suggestions: concepts[0]
              ? [`下一步優先練習「${concepts[0].name}」，完成後再自行檢查一次。`]
              : [],
            activities: mapExamHistory(history.entries),
          },
        ];
      }

      const students = z
        .array(teacherStudentSchema)
        .parse(await environment.apiClient.request<unknown>('/api/students'));

      const results = await Promise.allSettled(
        students.map(async (student): Promise<LearningReport> => {
          const encodedId = encodeURIComponent(student.id);
          const [generatedValue, topicsValue, historyValue] = await Promise.all([
            environment.apiClient.request<unknown>(
              `/api/students/${encodedId}/learning-report?range=${range}&lang=zh-TW`,
            ),
            environment.apiClient.request<unknown>(
              `/api/students/${encodedId}/weak-topics?range=${range}`,
            ),
            environment.apiClient.request<unknown>(
              `/api/students/${encodedId}/run-history?limit=50`,
            ),
          ]);
          const generated = generatedReportResponseSchema.parse(generatedValue);
          const topics = weakTopicsResponseSchema.parse(topicsValue);
          const report = generated.report;
          const history = examHistorySchema.parse(historyValue);
          const trend = student.progressTrend
            .map((point) => Math.round(point.correctRate))
            .filter((value) => value >= 0 && value <= 100);
          const concepts = (
            topics.weak_topics.length ? topics.weak_topics : report.weak_topics
          ).map((topic) => ({ name: topic.name, accuracy: topic.accuracy ?? 0 }));

          return {
            studentId: generated.student_id,
            studentName: report.student_name || student.name,
            period: `${displayDate(report.period_start)} – ${displayDate(report.period_end)}`,
            periodStart: report.period_start,
            periodEnd: report.period_end,
            mastery: report.accuracy_pct,
            completion:
              student.totalQuestions > 0
                ? Math.round((student.correctAnswers / student.totalQuestions) * 100)
                : 0,
            focus: 0,
            studyHours: report.study_hours,
            examsCompleted: report.exams_completed,
            questionsAnswered: report.questions_answered,
            trend: trend.length > 1 ? trend : [report.accuracy_pct],
            concepts,
            suggestions: report.ai_suggestions,
            activities: mapExamHistory(history.entries),
          };
        }),
      );

      return results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
    },
  });
}

function mapExamHistory(
  entries: z.infer<typeof examHistorySchema>['entries'],
): LearningReport['activities'] {
  return entries.map((entry, index) => {
    const submittedAt = entry.submitted_at ? new Date(entry.submitted_at) : null;
    const validDate = submittedAt && Number.isFinite(submittedAt.getTime()) ? submittedAt : null;
    const score =
      typeof entry.percentage === 'number'
        ? `${Math.round(entry.percentage)}%`
        : typeof entry.total_score === 'number' && typeof entry.max_score === 'number'
          ? `${entry.total_score} / ${entry.max_score}`
          : entry.status === 'graded'
            ? '已批改'
            : '已繳交';
    return {
      id: entry.assignment_exam_paper_id ?? `history-${index}`,
      title: `已完成活動 ${index + 1}`,
      dateLabel: validDate
        ? new Intl.DateTimeFormat('zh-TW', { month: 'numeric', day: 'numeric' }).format(validDate)
        : '完成日期未提供',
      contextLabel: entry.status === 'graded' ? '已完成批改' : '已繳交',
      resultLabel: score,
    };
  });
}
